import React, { FC } from 'react'
import { Flex } from '@moonpig/launchpad-components'
import { useStoreId } from '@moonpig/web-core-stores'
import { MULTI_CARD_DISCOUNTS } from '@moonpig/web-core-brand/config'
import { FromPrice } from '../../components/FromPrice'
import { PriceSuffix } from '../../components/PriceSuffix'
import { useProductStore } from '../../contexts/product'
import { ModuleContext, ModuleFromPrice } from '../types'
import { isCardProduct } from '../../utils/isCardProduct'
import { getDiscountedPriceCentAmount } from '../Price/getDiscountedPrice'

type Props = {
  module: ModuleFromPrice
  context: ModuleContext
}

export const FromPriceModule: FC<Props> = ({ module: { displayOptions } }) => {
  const [
    {
      product: { masterVariant, variants, category: productCategory },
      variant: selectedVariant,
      quantity,
    },
  ] = useProductStore()
  const storeId = useStoreId()

  const isCard = isCardProduct(productCategory.department)

  const cheapestVariant = variants.reduce((min, variant) => {
    return variant.price.centAmount < min.price.centAmount ? variant : min
  }, variants[0])

  const cardVariant = displayOptions?.showSelectedVariantPrice
    ? selectedVariant
    : masterVariant

  const variantPriceToShow = isCard ? cardVariant.price : cheapestVariant.price

  const discountedPercentage = isCard
    ? cardVariant.discountedPercentage
    : cheapestVariant.discountedPercentage

  const fullPrice = isCard ? cardVariant.fullPrice : cheapestVariant.fullPrice

  const discount = displayOptions?.showMultiCardDiscountedPrice
    ? MULTI_CARD_DISCOUNTS[storeId].reduce(
        (acc, current) =>
          quantity >= current.minimumQuantity ? current.discount : acc,
        0,
      )
    : 0
  const hasMultiCardDiscount = discount > 0

  const justifyContentUpSm = displayOptions?.align ?? 'left'
  const justifyContentXs = displayOptions?.alignXs ?? 'left'
  const alignItemsSm = justifyContentUpSm === 'left' ? 'flex-start' : 'flex-end'
  const alignItemsXs = justifyContentXs === 'left' ? 'flex-start' : 'flex-end'
  const inline = displayOptions?.inline ?? false

  return (
    <Flex
      data-testid="module-from-price"
      mb={6}
      justifyContent={{ xs: justifyContentXs, sm: justifyContentUpSm }}
      alignItems={{ xs: alignItemsXs, sm: alignItemsSm }}
      flexDirection={inline ? 'row' : 'column'}
      width="100%"
    >
      <FromPrice
        priceCentAmount={
          hasMultiCardDiscount
            ? getDiscountedPriceCentAmount(
                variantPriceToShow.centAmount,
                discount,
              )
            : variantPriceToShow.centAmount
        }
        currencyCode={variantPriceToShow.currencyCode}
        fractionDigits={variantPriceToShow.fractionDigits}
        fullPriceCentAmount={
          hasMultiCardDiscount ? undefined : fullPrice.centAmount
        }
        discountedPercentage={discountedPercentage}
        showFromPrice={displayOptions?.showFrom}
        color={displayOptions?.color}
      />
      <PriceSuffix inline={inline} color={displayOptions?.color} />
    </Flex>
  )
}
